import { Component, computed, inject, input, signal } from '@angular/core';

import { Image } from '@frontend/casino-ui';

import { ConfigProviderService } from '../config-provider/config-provider.service';
import { CasinoManager, CasinoStylesService, GameManagerService } from '../public-api';

interface ImageConfig {
    src: string;
    gameTileBorderRadius: string;
}

@Component({
    selector: 'cc-image-loader',
    templateUrl: 'image-loader.component.html',
    standalone: true,
    imports: [Image],
})
export class ImageLoaderComponent {
    icon = input.required<ImageConfig>();
    gameId = input.required<string>();
    iconType = input.required<number>();
    alt = input<string>('');
    priority = input<boolean>(false);
    disableTheme = input<boolean>(false);

    src = signal<string>('');
    defaultSrc = signal<string>('');
    host = signal<string>('');
    isImageLoaded = signal<boolean>(false);
    // This signal and lifecycle should be removed once grey image is under the cloudflare domain
    isImageLoadError = signal<boolean>(false);

    optimisedSrc = computed<string>(() => {
        return this.src();
    });

    computedStyle = computed<string>(() => this.getComputedStyle(this.icon()));

    private casinoManagerService = inject(CasinoManager);
    private configProviderService = inject(ConfigProviderService);
    private gameManagerService = inject(GameManagerService);
    private casinoStylesService = inject(CasinoStylesService);

    ngOnInit() {
        this.initialiseImageConfig();
    }

    private initialiseImageConfig() {
        let gameConfig = this.configProviderService.provideGameConfig();
        let defaultUrl = this.casinoManagerService.getGrayImageUrl();
        let src = this.icon().src;

        this.host.set(gameConfig.host);
        this.defaultSrc.set(defaultUrl);

        const feature = this.configProviderService.provideFeaturesConfig();
        if (feature.enableGameIconTheming) {
            src = this.casinoStylesService.appendTheme(src, this.gameId());
        }

        if (this.disableTheme()) {
            const isImageNotFound = this.gameManagerService.isGameIconNotAvailable(this.gameId(), this.iconType());
            src = isImageNotFound ? this.defaultSrc() : this.icon().src;
            isImageNotFound && this.isImageLoadError.set(true);
        }

        this.src.set(src || '');
    }

    getComputedStyle(icon: ImageConfig) {
        return `border-radius: ${icon.gameTileBorderRadius}; background-color: unset;`;
    }

    onLoad() {
        this.isImageLoaded.set(true);
    }

    onLoadError() {
        this.gameManagerService.setGameIconNotFound(this.gameId(), this.iconType());
        this.src.set(this.defaultSrc());
        this.isImageLoadError.set(true);
    }
}
